const PAGE_SIZE_MAX = 8;

class TpuBlogListing extends HTMLElement {
  connectedCallback() {
    this.filtersEl = this.querySelector('[data-tpu-listing-filters]');
    this.searchEl = this.querySelector('[data-tpu-listing-search]');
    this.gridEl = this.querySelector('[data-tpu-listing-grid]');
    this.cards = Array.from(this.querySelectorAll('[data-tpu-blog-card]'));
    this.emptyEl = this.querySelector('[data-tpu-listing-empty]');
    this.pagerEl = this.querySelector('[data-tpu-listing-pager]');
    this.pagesEl = this.querySelector('[data-tpu-listing-pages]');
    this.prevBtn = this.querySelector('[data-tpu-listing-prev]');
    this.nextBtn = this.querySelector('[data-tpu-listing-next]');
    this.stageEl = this.querySelector('.tpu-blog-listing__stage');

    this.pageSize = Math.min(
      PAGE_SIZE_MAX,
      Math.max(2, Number.parseInt(this.dataset.pageSize || String(PAGE_SIZE_MAX), 10) || PAGE_SIZE_MAX)
    );
    this.pageIndex = 0;
    this.activeTag = 'all';
    this.searchQuery = '';

    this.onFilterClick = this.onFilterClick.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onPagerClick = this.onPagerClick.bind(this);

    this.filtersEl?.addEventListener('click', this.onFilterClick);
    this.searchEl?.addEventListener('input', this.onSearchInput);
    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    this.pagesEl?.addEventListener('click', this.onPagerClick);

    this.applyFilters();
  }

  disconnectedCallback() {
    this.filtersEl?.removeEventListener('click', this.onFilterClick);
    this.searchEl?.removeEventListener('input', this.onSearchInput);
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.pagesEl?.removeEventListener('click', this.onPagerClick);
  }

  onFilterClick(event) {
    const btn = event.target.closest('[data-tpu-listing-tag]');
    if (!btn || !this.filtersEl?.contains(btn)) return;

    this.activeTag = btn.getAttribute('data-tpu-listing-tag') || 'all';
    this.pageIndex = 0;

    this.filtersEl.querySelectorAll('[data-tpu-listing-tag]').forEach((el) => {
      const active = el === btn;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.applyFilters();
  }

  onSearchInput(event) {
    this.searchQuery = String(event.target.value || '').trim().toLowerCase();
    this.pageIndex = 0;
    this.applyFilters();
  }

  onPrev() {
    if (this.pageIndex <= 0) return;
    this.pageIndex -= 1;
    this.applyFilters({ scroll: true });
  }

  onNext() {
    this.pageIndex += 1;
    this.applyFilters({ scroll: true });
  }

  onPagerClick(event) {
    const btn = event.target.closest('[data-tpu-listing-page]');
    if (!btn || !this.pagesEl?.contains(btn)) return;
    const page = Number.parseInt(btn.getAttribute('data-tpu-listing-page') || '0', 10);
    if (Number.isNaN(page) || page === this.pageIndex) return;
    this.pageIndex = page;
    this.applyFilters({ scroll: true });
  }

  matchesFilters(el) {
    const tags = String(el.getAttribute('data-tags') || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const category = String(el.getAttribute('data-category') || '').trim().toLowerCase();
    const searchBlob = String(el.getAttribute('data-search') || '').toLowerCase();
    const tagMatch =
      this.activeTag === 'all' || tags.includes(this.activeTag) || category === this.activeTag;
    const searchMatch = !this.searchQuery || searchBlob.includes(this.searchQuery);
    return tagMatch && searchMatch;
  }

  pageWindow(totalPages) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const current = this.pageIndex;
    const pages = new Set([0, totalPages - 1, current]);
    for (let i = current - 1; i <= current + 1; i += 1) {
      if (i > 0 && i < totalPages - 1) pages.add(i);
    }
    if (current <= 2) {
      pages.add(1);
      pages.add(2);
      pages.add(3);
    }
    if (current >= totalPages - 3) {
      pages.add(totalPages - 2);
      pages.add(totalPages - 3);
      pages.add(totalPages - 4);
    }

    return Array.from(pages).sort((a, b) => a - b);
  }

  renderPages(totalPages) {
    if (!this.pagesEl) return;

    if (totalPages <= 1) {
      this.pagesEl.innerHTML = '';
      return;
    }

    const windowPages = this.pageWindow(totalPages);
    const parts = [];
    let prev = null;

    windowPages.forEach((i) => {
      if (prev !== null && i - prev > 1) {
        parts.push('<span class="tpu-blog-listing__ellipsis" aria-hidden="true">…</span>');
      }
      const active = i === this.pageIndex;
      parts.push(
        `<button type="button" class="tpu-blog-listing__page${active ? ' is-active' : ''}" data-tpu-listing-page="${i}" aria-label="Page ${i + 1}"${active ? ' aria-current="page"' : ''}>${i + 1}</button>`
      );
      prev = i;
    });

    this.pagesEl.innerHTML = parts.join('');
  }

  applyFilters(options = {}) {
    const matching = this.cards.filter((card) => this.matchesFilters(card));
    const totalPages = Math.max(1, Math.ceil(matching.length / this.pageSize));
    if (this.pageIndex >= totalPages) this.pageIndex = Math.max(0, totalPages - 1);

    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    const pageCards = matching.slice(start, end);

    this.cards.forEach((card) => {
      card.hidden = true;
      card.classList.add('is-hidden');
      if (this.gridEl && card.parentElement !== this.gridEl) {
        this.gridEl.appendChild(card);
      }
    });

    pageCards.forEach((card) => {
      card.hidden = false;
      card.classList.remove('is-hidden');
      if (this.gridEl) this.gridEl.appendChild(card);
    });

    const canPrev = this.pageIndex > 0;
    const canNext = this.pageIndex < totalPages - 1 && matching.length > this.pageSize;

    if (this.prevBtn) {
      this.prevBtn.disabled = !canPrev;
      this.prevBtn.setAttribute('aria-disabled', canPrev ? 'false' : 'true');
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = !canNext;
      this.nextBtn.setAttribute('aria-disabled', canNext ? 'false' : 'true');
    }

    this.renderPages(totalPages);

    if (this.pagerEl) this.pagerEl.hidden = matching.length <= this.pageSize;
    if (this.emptyEl) this.emptyEl.hidden = matching.length > 0;
    if (this.gridEl) this.gridEl.hidden = pageCards.length === 0;

    if (options.scroll) {
      this.scrollToStage();
    }
  }

  scrollToStage() {
    const target = this.stageEl || this.gridEl;
    if (!target) return;

    const styles = getComputedStyle(document.documentElement);
    const sticky = parseFloat(styles.getPropertyValue('--tpu-sticky-header-height'));
    const header = parseFloat(styles.getPropertyValue('--header-height'));
    const offset = (Number.isFinite(sticky) && sticky > 0 ? sticky : Number.isFinite(header) && header > 0 ? header : 96) + 56;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }
}

if (!customElements.get('tpu-blog-listing')) {
  customElements.define('tpu-blog-listing', TpuBlogListing);
}
